import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import React, { useEffect, Fragment, useContext, lazy } from "react";
import { AppContext } from "../../provider/AppContext";
import Login from "../../redesign/auth/LoginForm";
import ForgotPassword from "../../redesign/auth/ForgotPassword";
import ResetPassword from "../../redesign/auth/ResetPassword";
import UpdateUserDetails from "../../redesign/Setting/UpdateProfile";
import { logoutUser, verifyProfile } from "../../services/AuthServices";
import { employeeDetail } from "../../services/EmployeeService";
import { executiveRoles } from "../NavBar/Navbar";
import BrandLoader from "../Loaders/BrandLoader";
const CreateUserProfile = lazy(() => import("../User/CreateUserProfile"));
const EmbedTrello = lazy(() => import("../EmbedTrello/Index"));
const PrivacyPolicy = lazy(() => import("../../pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("../../pages/Terms"));
const CKTemplateEditor = lazy(() => import("../Template/Index"));
const ContractTemplate = lazy(() => import("../Setting/ContractTemplates"));
const TemplateManager = lazy(() => import("../Setting/TemplateManager"));
const ClientProfiles = lazy(() => import("../Setting/ClientProfiles"));
const AllPricing = lazy(() => import("../Pricing/AllPricing.js"));
const ContractOverview = lazy(() => import("../Contracts/ContractOverview"));
const ErrorPage = lazy(() => import("../../pages/ErrorPage"));
const Navbar = lazy(() => import("../NavBar/Navbar"));
const SignUpForm = lazy(() => import("../../auth/SignupForm"));
const EditUser = lazy(() => import("../User/EditUser"));
const UpdatePassword = lazy(() => import("../Setting/UpdatePassword"));
const EmployeesRoute = lazy(() => import("../Setting/Employees"));
const TeamsRoute = lazy(() => import("../Setting/TeamsPanel"));
const ClientLinksRoute = lazy(() => import("../Setting/ClientLinksPanel"));
const UserAccessRoute = lazy(() => import("../Setting/UserAccessPanel"));
const RoleDefaultsRoute = lazy(() => import("../Setting/RoleDefaultsPanel"));
const PermissionsCatalogRoute = lazy(() =>
  import("../Setting/PermissionsCatalogPanel")
);
const AccessControlHubRoute = lazy(() =>
  import("../Setting/access/AccessControlHub")
);
const RestoreContract = lazy(() => import("../Setting/RestoreContractTemplate"));
const AdobeSignWrapper = lazy(() =>
  import("../Contracts/AdobeSign/AdobeSignWrapper")
);
const UpdateUserProfile = lazy(() => import("../Setting/UpdateUserProfiles"));
const ReportsTable = lazy(() => import("../Contracts/ReportsTable"));
const Statistics = lazy(() => import("../Contracts/StatsOverview"));
const GraphsView = lazy(() => import("../Contracts/GraphOverview"));
const ExpenseOverview = lazy(() => import("../Contracts/ExpenseOverview"));
const ProgressReport = lazy(() => import("../ProgressReport/ProgressReport"));
const CustomersDashboard = lazy(() => import("../Contracts/BarTable/index"));

function WebRoutes() {
  const {
    isAuthenticated,
    isLoading,
    setLoading,
    socket,
    initialFetched,
    setIsAuthenticated,
    handleReset,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const checkAuthenticated = async () => {
    if (!localStorage.token) return setLoading(false);
    try {
      const res = await verifyProfile();
      res ? setIsAuthenticated(true) : setIsAuthenticated(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err.message);
    }
  };

  useEffect(() => {
    checkAuthenticated();
    // eslint-disable-next-line
  }, []);

  const setAuth = (boolean) => {
    setIsAuthenticated(boolean);
  };

  const AuthLoginNew = () => {
    return !isAuthenticated ? (
      <Login setAuth={setAuth} />
    ) : (
      <Navigate to="/stats/table" />
    );
  };

  const logout = async (e) => {
    await logoutUser();
    e.preventDefault();
    try {
      setAuth(false);
      handleReset();
      socket.emit("logout");
      // The next sign-in must open a new socket with its own token (project-management#504).
      socket.disconnect();
      initialFetched.current = false;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    } catch (err) {
      console.error(err.message);
    }
  };

  const routes = [
    {
      path: "/",
      Component: () => <Navigate to="/stats/table" replace />,
      allowedPrivate: true,
    },
    {
      path: "/home",
      Component: () => <Navigate to="/stats/table" replace />,
      allowedPrivate: true,
    },
    {
      path: "/pricing/:id",
      Component: AllPricing,
      allowedPrivate: true,
    },
    {
      path: "/createemployee",
      Component: CreateUserProfile,
      allowedPrivate: true,
    },
    {
      path: "/account",
      Component: EditUser,
      allowedPrivate: true,
    },
    {
      path: "/contracts/:tabid",
      Component: ContractOverview,
      allowedPrivate: true,
    },
    {
      path: "/contracts",
      Component: ContractOverview,
      allowedPrivate: true,
    },
    {
      path: "/stats/:tabid",
      Component: ContractOverview,
      allowedPrivate: true,
    },
    {
      path: "/stats",
      Component: Statistics,
      allowedPrivate: true,
    },
    {
      path: "/stats/table",
      Component: Statistics,
      allowedPrivate: true,
    },
    {
      path: "/stats/graph",
      Component: GraphsView,
      allowedPrivate: true,
    },
    {
      path: "/stats/expense",
      Component: ExpenseOverview,
      allowedPrivate: true,
    },
    {
      path: "/stats/progress",
      Component: ProgressReport,
      allowedPrivate: true,
    },
    {
      path: "/customers",
      Component: CustomersDashboard,
      allowedPrivate: true,
    },
    {
      path: "/vendors",
      Component: () => (
        <div className="tw-min-h-screen tw-bg-gray-50 tw-py-8 tw-flex tw-items-center tw-justify-center">
          <div className="tw-text-center">
            <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-800 tw-mb-4">Vendors</h1>
            <p className="tw-text-gray-600">Coming soon...</p>
          </div>
        </div>
      ),
      allowedPrivate: true,
    },
    {
      path: "/templates",
      Component: TemplateManager,
      allowedPrivate: true,
    },
    {
      path: "/client",
      Component: ClientProfiles,
      allowedPrivate: true,
    },
    {
      // The bare editor edited the Default template silently (#283). Send it to
      // the workspace so nobody lands on an unlabeled default editor; editing a
      // specific template still uses /template-contract/edit/:id below.
      path: "/template-contract",
      Component: () => <Navigate to="/templates" replace />,
      allowedPrivate: true,
    },
    {
      path: "/template-contract/:id",
      Component: CKTemplateEditor,
      allowedPrivate: true,
    },
    {
      path: "/template-contract/:mode/:id",
      Component: CKTemplateEditor,
      allowedPrivate: true,
    },
    {
      path: "/recurring-report",
      Component: ReportsTable,
      allowedPrivate: true,
      isAdminRoute: true,
    },

    {
      path: "/login",
      Component: AuthLoginNew,
    },
    {
      path: "/privacy-policy",
      Component: PrivacyPolicy,
    },
    {
      path: "/terms-of-service",
      Component: TermsAndConditions,
    },
    {
      path: "/login_new",
      Component: AuthLoginNew,
    },
    {
      path: "/reset",
      Component: ForgotPassword,
    },

    {
      path: "/reset_new",
      Component: ForgotPassword,
    },
    {
      path: "/confirm_reset/:token",
      Component: ResetPassword,
    },
    {
      path: "/confirm_reset_new/:token",
    },
    {
      path: "/signup",
      Component: SignUpForm,
    },
    {
      path: "/password",
      Component: UpdatePassword,
      allowedPrivate: true,
    },
    {
      path: "/profile",
      Component: UpdateUserDetails,
      allowedPrivate: true,
    },
    {
      path: "/profile_new",
      Component: UpdateUserDetails,
      allowedPrivate: true,
    },
    {
      path: "/employees",
      Component: EmployeesRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/access-control",
      Component: AccessControlHubRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/teams",
      Component: TeamsRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/client-links",
      Component: ClientLinksRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/user-access",
      Component: UserAccessRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/role-defaults",
      Component: RoleDefaultsRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/permissions-catalog",
      Component: PermissionsCatalogRoute,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/restore-contract",
      Component: RestoreContract,
      allowedPrivate: true,
    },
    {
      path: "/approved-contract-templates",
      Component: ContractTemplate,
      allowedPrivate: true,
    },
    {
      path: "/adobesign",
      Component: AdobeSignWrapper,
    },
    {
      path: "/employees/:id",
      Component: UpdateUserProfile,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/reports",
      Component: ReportsTable,
      allowedPrivate: true,
      isAdminRoute: true,
    },
    {
      path: "/*",
      Component: ErrorPage,
    },
  ];

  const { user, setUser } = useContext(AppContext);

  useEffect(() => {
    if (!user && localStorage.token)
      employeeDetail()
        .then((data) => {
          if (!data?.employee) return setIsAuthenticated(false);

          setUser(data.employee);
        })
        .catch(({ data }) => {
          if (data.message) {
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            navigate("/login");
          }
        });
    // eslint-disable-next-line
  }, [user, setUser]);

  const { role } = user || {};
  const isAdmin = executiveRoles.includes(role);

  const isUser =
    role === "event director" ||
    role === "Event Director" ||
    role === "sale" ||
    role === "Sale" ||
    role === "Event Director Assistant" ||
    role === "Assistant" ||
    role === "event director assistant";

  return (
    <Fragment>
      <Navbar
        isAdmin={isAdmin}
        isUser={isUser}
        isAuthenticated={isAuthenticated}
        logout={logout}
      />
      {isLoading ? (
        <BrandLoader fullScreen />
      ) : (
        <React.Suspense fallback={<BrandLoader fullScreen />}>
          <Routes>
            {routes.map(
              ({ path, Component, allowedPrivate, isAdminRoute }, index) => {
                const isPrivate = !isAuthenticated && allowedPrivate;
                const isAllowedRoute = ["Admin", "Owner"].includes(user?.role);

                return (
                  <Route
                    path={path}
                    key={index}
                    element={
                      isPrivate ? (
                        <Navigate to="/login" />
                      ) : isAdminRoute && !isAllowedRoute ? (
                        <Navigate to="/home" />
                      ) : (
                        <Component setAuth={setAuth} />
                      )
                    }
                  />
                );
              }
            )}
          </Routes>
        </React.Suspense>
      )}
    </Fragment>
  );
}

export default WebRoutes;
